import {
  ensureGpuLayersForList,
  gpuCompositeLayers,
} from './canvasGpuCore';
// client/src/coms/views/canvas_parts/canvas_room_view_parts/layerUtils.js
let layerIdCounter = 0;

export function createLayerId() {
  layerIdCounter = (layerIdCounter + 1) >>> 0;
  return `layer-${Date.now()}-${layerIdCounter.toString(36).padStart(4, '0')}`;
}

export function createSharedLayerDefaultName(index) {
  const safeIndex = Number.isInteger(Number(index)) ? Number(index) : 0;
  return `Layer ${safeIndex + 1}`;
}

export function createUserLayerDefaultName(index, ownerName = '') {
  const safeIndex = Number.isInteger(Number(index)) ? Number(index) : 0;
  const safeOwnerName = String(ownerName || '').trim();
  return `${safeOwnerName || 'My'} layer ${safeIndex + 1}`;
}

export function createLayerDefaultName(index, ownerName = '', options = {}) {
  const safeOwnerName = String(ownerName || '').trim();
  if (options && options.shared === true) return createSharedLayerDefaultName(index);
  return safeOwnerName ? createUserLayerDefaultName(index, safeOwnerName) : createSharedLayerDefaultName(index);
}

export function sanitizeLayerName(name, fallback = '') {
  const safeName = String(name || '').replace(/\s+/g, ' ').trim().slice(0, 96);
  const safeFallback = String(fallback || '').replace(/\s+/g, ' ').trim().slice(0, 96);
  return safeName || safeFallback || 'Untitled layer';
}

export function createLayerMeta(index, overrides = {}) {
  const defaultName = createLayerDefaultName(index, overrides.ownerName, {
    shared: overrides.sharedLayer === true || overrides.editableByOthers === true,
  });
  const nextLayer = {
    id: createLayerId(),
    name: sanitizeLayerName(overrides.name, defaultName),
    visible: true,
    opacity: 1,
    ...overrides,
    name: sanitizeLayerName(overrides.name, defaultName),
  };

  if (!String(nextLayer.id || '').trim()) {
    nextLayer.id = createLayerId();
  }

  return nextLayer;
}

export function getLayerOwnerUserId(layer) {
  const ownerUserId = Number(layer && layer.ownerUserId);
  if (Number.isInteger(ownerUserId) && ownerUserId > 0) return ownerUserId;

  const rawId = String(layer && layer.id ? layer.id : '').trim();
  const match = rawId.match(/^remote-layer-user-(\d+)$/) || rawId.match(/^remote-layer-user-(\d+)-/);
  if (match && Number.isInteger(Number(match[1]))) return Number(match[1]);

  const ownerKey = String(layer && layer.ownerKey ? layer.ownerKey : '').trim();
  const ownerKeyMatch = ownerKey.match(/^user-(\d+)$/);
  if (ownerKeyMatch && Number.isInteger(Number(ownerKeyMatch[1]))) return Number(ownerKeyMatch[1]);

  return 0;
}

export const USER_LAYER_COLOURS = ['#ef4444', '#3b82f6', '#facc15', '#22c55e'];

export function getLayerOwnerKey(layer) {
  if (!layer || typeof layer !== 'object') return '';
  const explicitKey = String(layer.ownerKey || '').trim();
  if (explicitKey) return explicitKey;
  const ownerUserId = Number(layer.ownerUserId);
  if (Number.isInteger(ownerUserId) && ownerUserId > 0) return `user-${ownerUserId}`;
  const ownerClientId = String(layer.ownerClientId || '').trim();
  if (ownerClientId) return `client-${ownerClientId}`;
  const rawId = String(layer.id || '').trim();
  const match = rawId.match(/^remote-layer-(user-\d+|client-[a-zA-Z0-9_-]+)$/);
  return match ? match[1] : '';
}

export function getUserLayerColour(ownerKeyOrLayer) {
  const ownerKey = typeof ownerKeyOrLayer === 'string'
    ? ownerKeyOrLayer.trim()
    : getLayerOwnerKey(ownerKeyOrLayer);
  if (!ownerKey) return '';
  let hash = 0;
  for (let index = 0; index < ownerKey.length; index += 1) {
    hash = ((hash * 31) + ownerKey.charCodeAt(index)) >>> 0;
  }
  return USER_LAYER_COLOURS[hash % USER_LAYER_COLOURS.length];
}

export function isLayerOwnedByViewer(layer, viewer = {}) {
  if (!layer) return false;

  const viewerUserId = Number(viewer && viewer.currentUserId);
  const layerOwnerUserId = getLayerOwnerUserId(layer);
  if (Number.isInteger(viewerUserId) && viewerUserId > 0 && layerOwnerUserId > 0) {
    return layerOwnerUserId === viewerUserId;
  }

  const viewerClientId = String(viewer && viewer.currentClientId ? viewer.currentClientId : '').trim();
  const layerOwnerClientId = String(layer && layer.ownerClientId ? layer.ownerClientId : '').trim();
  if (viewerClientId && layerOwnerClientId) return viewerClientId === layerOwnerClientId;

  const viewerName = String(viewer && viewer.currentUserName ? viewer.currentUserName : '').trim().toLowerCase();
  const ownerName = String(layer && layer.ownerName ? layer.ownerName : '').trim().toLowerCase();
  if (viewerName && ownerName) return viewerName === ownerName;

  if (layer.isRemoteUserLayer) return false;
  return true;
}

export function isLayerVisibleToViewer(layer, viewer = {}) {
  if (!layer || layer.visible === false) return false;
  if (layer.hiddenFromOthers !== true) return true;
  return isLayerOwnedByViewer(layer, viewer);
}

export function moveArrayItem(items, fromIndex, toIndex) {
  if (fromIndex === toIndex) return items;
  if (fromIndex < 0 || toIndex < 0 || fromIndex >= items.length || toIndex >= items.length) {
    return items;
  }

  const next = [...items];
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return next;
}

const sharedCanvasToolWebglState = {
  canvas: null,
  gl: null,
};

export function getSharedCanvasToolWebglSurface(targetCanvasOrWidth, widthOrHeight = 1, height = 1) {
  const hasCanvasTarget = Boolean(
    targetCanvasOrWidth
    && typeof targetCanvasOrWidth === 'object'
    && Number.isFinite(Number(targetCanvasOrWidth.width))
    && Number.isFinite(Number(targetCanvasOrWidth.height)),
  );

  const targetCanvas = hasCanvasTarget ? targetCanvasOrWidth : null;
  const safeWidth = Math.max(
    1,
    Math.floor(Number(hasCanvasTarget ? widthOrHeight : targetCanvasOrWidth) || Number(targetCanvas && targetCanvas.width) || 1),
  );
  const safeHeight = Math.max(
    1,
    Math.floor(Number(hasCanvasTarget ? height : widthOrHeight) || Number(targetCanvas && targetCanvas.height) || 1),
  );

  if (!sharedCanvasToolWebglState.canvas && typeof document !== 'undefined') {
    const canvas = document.createElement('canvas');
    canvas.width = safeWidth;
    canvas.height = safeHeight;
    const gl = canvas.getContext('webgl', {
      alpha: true,
      antialias: false,
      depth: false,
      stencil: false,
      preserveDrawingBuffer: true,
      premultipliedAlpha: false,
      powerPreference: 'high-performance',
      desynchronized: true,
    });
    if (!gl) return null;
    sharedCanvasToolWebglState.canvas = canvas;
    sharedCanvasToolWebglState.gl = gl;
  }

  const { canvas, gl } = sharedCanvasToolWebglState;
  if (!canvas || !gl || gl.isContextLost()) return null;
  if (canvas.width !== safeWidth) canvas.width = safeWidth;
  if (canvas.height !== safeHeight) canvas.height = safeHeight;
  return {
    canvas,
    gl,
    width: canvas.width,
    height: canvas.height,
    targetCanvas,
  };
}

export function compositeLayers(displayCanvas, layers, layerCanvasMap, viewer = {}, gpuLayerStoreRef = null, rendererRef = null) {
  void layerCanvasMap;

  if (!displayCanvas) {
    return 'missing-display-canvas';
  }

  const safeLayers = Array.isArray(layers) ? layers : [];
  const renderer = ensureGpuLayersForList(
    gpuLayerStoreRef,
    displayCanvas,
    safeLayers,
    displayCanvas.width,
    displayCanvas.height,
    rendererRef,
  );

  if (!renderer) {
    return 'gpu-unavailable';
  }

  const composed = gpuCompositeLayers(
    displayCanvas,
    safeLayers,
    null,
    viewer,
    isLayerVisibleToViewer,
  );

  if (!composed) {
    return 'gpu-failed';
  }

  return 'gpu';
}
